import React from 'react';
import { useEffect } from 'react';
import { readStore } from '../helpers/persistence';
import { ILoggedUser } from '../models/User.Model';
import AuthorizationService from '../services/AuthorizationContextService'
import * as http from '../helpers/http';

interface IProps { children: React.ReactNode; }
interface IContextResult {
  user?: ILoggedUser,
  isAutenticated: () => boolean,
  loading: boolean,
  hasAccess: (rule: string) => boolean,
  login: (username: String, password: String) => Promise<ILoggedUser | undefined>,
  logout: () => void,
  getConfig: () => any
}

const Context = React.createContext<IContextResult>({
  user: undefined,
  isAutenticated: () => false,
  loading: false,
  hasAccess: (id: string) => false,
  login: (username: string, password: string) => Promise.resolve(undefined),
  logout: () => console.warn('no provider'),
  getConfig: undefined,
});

const AuthorizationProvider = ({ children }: IProps) => {
  const [user, setUser] = React.useState<ILoggedUser | undefined>(undefined);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [supportKey, setSupportKey] = React.useState<boolean>(true);
  const [config, setConfig] = React.useState<any>(undefined);

  function getCookie(name, remove = false) {
    const match = document.cookie.match(new RegExp(
      `(?:^|; )${name.replace(/([.*+?^=!:${}()|[\]\\])/g, '\\$1')}=([^;]*)`
    ));

    if (remove) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }

    return match ? decodeURIComponent(match[1]) : undefined;
  }

  useEffect(() => {
    !config && AuthorizationService.config().then(r => {
      setConfig(r.data);
      }).catch(() => setLoading(false));

    if (!user && !loading) {
      const token = getCookie('token', true);
      if (token) {
        let stringTonken = `"${token}"`;
        localStorage.setItem("token", stringTonken);

        const refreshToken = getCookie('refreshToken', true);
        if (refreshToken) {
          let stringRefreshTonken = `"${refreshToken}"`;
          localStorage.setItem("refresh-token", stringRefreshTonken);
        }

        setLoading(true);
        AuthorizationService.me()
          .then(u => {
            setUser(u);
            setLoading(false)
          }).catch(() => setLoading(false));
      } else {
        readStore('token')
          .then(token => {
            if (token) {
              setLoading(true);
              AuthorizationService.me()
                .then(u => {
                  setUser(u);
                  setLoading(false)
                }).catch(() => setLoading(false))
            }
          });
      }
    }
  }, []);

  const logout = () => setUser(undefined);

  const login = (username: String, password: String): Promise<ILoggedUser | undefined> => {
    setLoading(true);
    return AuthorizationService
      .login(username, password)
      .then(u => {
        setLoading(false);
        setUser(u);
        return u;
      })
      .catch(error => {
        setLoading(false);
        throw error;
      });
  }

  const hasAccess = (rule: string) => user && user.roles && Array.isArray(user.roles) && user.roles.length > 0 ? user.roles.indexOf(rule) > 0 : true;
  const isAutenticated = () => !!(!!user && user.id > 0 && localStorage.getItem('token') && localStorage.getItem('token') !== '');
  const getConfig = () => config;

  return (
    <Context.Provider value={{ user, isAutenticated, loading, login, logout, hasAccess, getConfig }}>
      {children}
    </Context.Provider>
  );
}

export { AuthorizationProvider };
export const useAuthorization = () => React.useContext(Context);
